/**
 * Source unique des chiffres de l'espace pro.
 *
 * UNE lecture allégée par type de document (en-têtes seuls, paginés), partagée
 * par le tableau de bord ET les pages clients via le cache TanStack Query.
 * Toute l'agrégation vit dans `@erp/domain/pro` — ici on ne fait que brancher.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  computeActivitySummary,
  computeClientStats,
  computeProDashboard,
  filterByRange,
  resolvePreviousStatsRange,
  resolveStatsRange,
  type ProActivitySummary,
  type ProClientStatRow,
  type ProDashboardSnapshot,
  type ProStatsRange,
} from '@erp/domain/pro';
import type { ProCommandeSummary, ProDevisSummary, ProWalletMovement } from '@erp/data-access/interfaces';
import { useDataService } from '@/lib/service-context';

/** Les documents bougent peu : 60 s évite une rafale de refetch au moindre onglet. */
const STALE_MS = 60_000;

export const PRO_DASHBOARD_KEYS = {
  commandes: ['pro-commande-summaries'] as const,
  devis: ['pro-devis-summaries'] as const,
  wallet: ['pro-wallet-movements', 'all'] as const,
  clients: ['pro-clients'] as const,
};

export interface ProDashboardData extends ProDashboardSnapshot {
  commandes: ProCommandeSummary[];
  devis: ProDevisSummary[];
  /** Grand livre brut. `wallet` (hérité du snapshot) porte les totaux calculés. */
  walletMovements: ProWalletMovement[];
  /** Même période, un cran plus tôt. `null` sur « depuis toujours ». */
  previousActivity: ProActivitySummary | null;
  isLoading: boolean;
  isError: boolean;
  /** `true` tant qu'aucun document n'existe — sert à afficher l'état vide. */
  isEmpty: boolean;
}

/**
 * Instantané complet pour une période.
 *
 * ⚠ `range` ne filtre QUE l'activité et la ventilation client : le portefeuille
 * reste cumulé depuis l'origine (un solde disponible « ce mois-ci » n'existe pas).
 */
export function useProDashboard(range: ProStatsRange): ProDashboardData {
  const ds = useDataService();

  const commandesQuery = useQuery({
    queryKey: PRO_DASHBOARD_KEYS.commandes,
    queryFn: () => ds.proCommandes.listSummaries(),
    staleTime: STALE_MS,
  });
  const devisQuery = useQuery({
    queryKey: PRO_DASHBOARD_KEYS.devis,
    queryFn: () => ds.proDevis.listSummaries(),
    staleTime: STALE_MS,
  });
  const walletQuery = useQuery({
    queryKey: PRO_DASHBOARD_KEYS.wallet,
    queryFn: () => ds.proWallet.listMovements(),
    staleTime: STALE_MS,
  });

  const commandes = useMemo(() => commandesQuery.data ?? [], [commandesQuery.data]);
  const devis = useMemo(() => devisQuery.data ?? [], [devisQuery.data]);
  const wallet = useMemo(() => walletQuery.data ?? [], [walletQuery.data]);

  const snapshot = useMemo(
    () =>
      computeProDashboard({
        commandes,
        devis,
        wallet,
        // `new Date()` à chaque recalcul : les bornes ne changent qu'au
        // changement de jour, et le memo ne dépend que des données + du range.
        bounds: resolveStatsRange(range, new Date()),
      }),
    [commandes, devis, wallet, range],
  );

  const previousActivity = useMemo(() => {
    const bounds = resolvePreviousStatsRange(range, new Date());
    if (!bounds) return null;
    return computeActivitySummary(
      filterByRange(commandes, bounds),
      filterByRange(devis, bounds),
    );
  }, [commandes, devis, range]);

  return {
    ...snapshot,
    commandes,
    devis,
    walletMovements: wallet,
    previousActivity,
    isLoading: commandesQuery.isLoading || devisQuery.isLoading || walletQuery.isLoading,
    isError: commandesQuery.isError || devisQuery.isError || walletQuery.isError,
    isEmpty: !commandesQuery.isLoading && commandes.length === 0 && devis.length === 0,
  };
}

/**
 * Chiffres par client, toutes périodes confondues, indexés par id.
 *
 * ⚠ `pro_clients.depense` / `.commission` / `.solde_cumule` existent en base
 * mais AUCUN trigger ne les alimente : elles valent 0 pour tout le monde. Les
 * pages clients doivent lire ce hook, jamais ces colonnes.
 */
export function useProClientStats(): {
  byClientId: Map<string, ProClientStatRow>;
  rows: ProClientStatRow[];
  isLoading: boolean;
} {
  const ds = useDataService();

  const commandesQuery = useQuery({
    queryKey: PRO_DASHBOARD_KEYS.commandes,
    queryFn: () => ds.proCommandes.listSummaries(),
    staleTime: STALE_MS,
  });
  const devisQuery = useQuery({
    queryKey: PRO_DASHBOARD_KEYS.devis,
    queryFn: () => ds.proDevis.listSummaries(),
    staleTime: STALE_MS,
  });

  const rows = useMemo(
    () => computeClientStats(commandesQuery.data ?? [], devisQuery.data ?? []),
    [commandesQuery.data, devisQuery.data],
  );

  const byClientId = useMemo(() => {
    const map = new Map<string, ProClientStatRow>();
    for (const row of rows) if (row.clientId) map.set(row.clientId, row);
    return map;
  }, [rows]);

  return {
    byClientId,
    rows,
    isLoading: commandesQuery.isLoading || devisQuery.isLoading,
  };
}

/** Ligne neutre pour un client sans aucun document — évite les `?? 0` partout. */
export const EMPTY_CLIENT_STATS: ProClientStatRow = {
  clientId: null,
  ordersCount: 0,
  quotesCount: 0,
  revenueTtc: 0,
  collected: 0,
  paidWithWallet: 0,
  commission: 0,
  quotedTtc: 0,
  openQuotesCount: 0,
  openQuotesTtc: 0,
  firstOrderDate: null,
  lastOrderDate: null,
  lastActivityDate: null,
  averageOrderValue: 0,
  revenueShare: 0,
  commissionRate: 0,
};
